import * as R from 'ramda'
import { forwardRef } from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { TZDate } from '@date-fns/tz'
import {
  getDate,
  getHours,
  getMilliseconds,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  set,
} from 'date-fns'
import { pipe } from 'fp-ts/lib/function'

export type BaseProps = {
  name?: string
  className?: string
  timezone?: string
}

export type UseFormProps<T extends BaseProps> = T & {
  form: UseFormReturn
  defaultValue?: Date
}

export type UseStateProps<T extends BaseProps> = T & {
  date: Date
  setDate: (date: Date | undefined) => void
}

export type Props<T extends BaseProps = BaseProps> =
  | UseStateProps<T>
  | UseFormProps<T>

export const formExists = <T extends BaseProps>(
  props: Partial<Props>,
): props is UseFormProps<T> => 'form' in props

export const stateExists = <T extends BaseProps>(
  props: Partial<Props>,
): props is UseStateProps<T> => 'date' in props

export const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
export const allTimezones = Intl.supportedValuesOf('timeZone')

export const datePartsEqual = (
  date1: Date,
  date2: Date,
  parts: {
    year?: boolean
    month?: boolean
    date?: boolean
    hours?: boolean
    minutes?: boolean
    seconds?: boolean
    milliseconds?: boolean
  },
) => {
  const getters = {
    year: getYear,
    month: getMonth,
    date: getDate,
    hours: getHours,
    minutes: getMinutes,
    seconds: getSeconds,
    milliseconds: getMilliseconds,
  }

  const predicates = pipe(
    parts,
    R.filter(R.equals(true)),
    R.keys,
    (parts) => R.pick(parts, getters),
    R.values,
    R.map(
      (fn) => (date1: Date) => (date2: Date) => R.equals(fn(date1), fn(date2)),
    ),
  )

  return pipe(date1, R.allPass(predicates.map((fn) => fn(date2))))
}

export const removeSecondsMilliseconds = (date: Date) =>
  set(date, {
    seconds: 0,
    milliseconds: 0,
  })
